import React, { useEffect, useState, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DateToLocale, ToFirstCapital } from "../../Utils/helpers";
import { viewSiteVisit } from "../../Redux/Actions/siteVisitActions";
import axios from "axios";
import toast from "react-hot-toast";
import MainLayout from "../../Layout/mainLayout";
import "./viewAgentSiteVisit.css";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

/* ── Leaflet map rendered inside a card ── */
const AgentRouteMap = ({ startLocation, agentLocations, dropLocation }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    // Load Leaflet CSS once
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id = "leaflet-css";
      link.rel = "stylesheet";
      link.href = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css";
      document.head.appendChild(link);
    }

    // Load Leaflet JS then init map
    const initMap = () => {
      if (mapInstanceRef.current || !mapRef.current) return;
      const L = window.L;

      // Filter out dummy/default coordinates (Google HQ & SF defaults)
      const DUMMY_COORDS = [
        [37.421998333333335, -122.084],
        [37.785834, -122.406417],
      ];
      const isDummy = (lat, lng) =>
        DUMMY_COORDS.some(([dlat, dlng]) => Math.abs(lat - dlat) < 0.0001 && Math.abs(lng - dlng) < 0.0001);

      const validAgentPoints = (agentLocations || [])
        .filter((p) => !isDummy(p.lat, p.lng))
        .map((p) => [p.lat, p.lng]);

      const startLatLng = startLocation
        ? [startLocation.latitude, startLocation.longitude]
        : validAgentPoints[0] || null;

      // drop_location is GeoJSON format: coordinates = [lng, lat]
      const dropLatLng =
        dropLocation?.coordinates?.length === 2
          ? [dropLocation.coordinates[1], dropLocation.coordinates[0]]
          : null;

      if (!startLatLng && validAgentPoints.length === 0 && !dropLatLng) return;

      const allPoints = [
        ...(startLatLng ? [startLatLng] : []),
        ...validAgentPoints,
        ...(dropLatLng ? [dropLatLng] : []),
      ];

      const map = L.map(mapRef.current, { zoomControl: true, scrollWheelZoom: false });
      mapInstanceRef.current = map;

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap contributors",
        maxZoom: 19,
      }).addTo(map);

      // ── Start marker (green) ──
      if (startLatLng) {
        const startIcon = L.divIcon({
          html: `<div class="vsv-map-marker vsv-map-marker--start">
                   <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                     <circle cx="12" cy="12" r="10"/>
                   </svg>
                 </div>`,
          className: "",
          iconSize: [32, 32],
          iconAnchor: [16, 16],
        });
        L.marker(startLatLng, { icon: startIcon })
          .addTo(map)
          .bindPopup("<b>Start Location</b>");
      }

      // ── Agent trail polyline ──
      if (validAgentPoints.length > 1) {
        L.polyline(validAgentPoints, {
          color: "#0D152B",
          weight: 3,
          opacity: 0.75,
          dashArray: "6 4",
        }).addTo(map);
      }

      // ── Agent location dots (numbered) ──
      validAgentPoints.forEach((point, idx) => {
        const isLast = idx === validAgentPoints.length - 1;
        const dotIcon = L.divIcon({
          html: `<div class="vsv-map-dot-numbered ${isLast ? "vsv-map-dot-numbered--last" : ""}">
                   <span class="vsv-map-dot-number">${idx + 1}</span>
                 </div>`,
          className: "",
          iconSize: [22, 22],
          iconAnchor: [11, 11],
        });
        L.marker(point, { icon: dotIcon })
          .addTo(map)
          .bindPopup(isLast ? `<b>Last Known Position</b><br/>Point ${idx + 1}` : `Point ${idx + 1}`);
      });

      // ── Drop location marker (orange pin) ──
      if (dropLatLng) {
        const dropIcon = L.divIcon({
          html: `<div class="vsv-map-marker vsv-map-marker--drop">
                   <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                     <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/>
                     <circle cx="12" cy="10" r="3" fill="white"/>
                   </svg>
                 </div>`,
          className: "",
          iconSize: [32, 32],
          iconAnchor: [16, 16],
        });
        L.marker(dropLatLng, { icon: dropIcon })
          .addTo(map)
          .bindPopup(
            `<b>Drop Location</b><br/><span style="font-size:11px;color:#666">${dropLatLng[0].toFixed(6)}, ${dropLatLng[1].toFixed(6)}</span>`
          );
      }

      // ── Fit bounds ──
      if (allPoints.length > 1) {
        map.fitBounds(L.latLngBounds(allPoints), { padding: [40, 40] });
      } else {
        map.setView(allPoints[0], 16);
      }
    };

    if (window.L) {
      initMap();
    } else {
      const script = document.createElement("script");
      script.src = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js";
      script.onload = initMap;
      document.head.appendChild(script);
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [startLocation, agentLocations, dropLocation]);

  return <div ref={mapRef} className="vsv-map-container" />;
};

/* ═══════════════════════════════════════════════════════════ */

const ViewAgentSiteVisit = () => {
  const { id } = useParams();
  const { siteVisit } = useSelector((state) => state.siteVisit);
  const dispatch = useDispatch();

  const [tripStatus, setTripStatus] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    dispatch(viewSiteVisit(id)).finally(() => setLoading(false));
  }, [id, dispatch]);

  useEffect(() => {
    if (siteVisit?.data?.trip_status) {
      setTripStatus(siteVisit.data.trip_status);
    }
  }, [siteVisit]);

  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
    setShowConfirm(true);
  };

  const confirmStatusChange = async () => {
    setIsUpdating(true);
    setShowConfirm(false);
    try {
      await axios.post(`${BASE_URL}/site-visit/update`, {
        id: siteVisit?.data?._id,
        status: selectedStatus,
      });
      toast.success("Status updated successfully!");
      await dispatch(viewSiteVisit(id));
      setTripStatus(selectedStatus);
    } catch {
      toast.error("Error updating status");
    } finally {
      setIsUpdating(false);
    }
  };

  const data = siteVisit?.data;
  const isSelf = data?.site_visit_by === "self";

  if (loading) {
    return (
      <MainLayout>
        <div className="vsv-loader-wrap">
          <div className="vsv-loader" />
          <p className="vsv-loader-text">Loading site visit data…</p>
        </div>
      </MainLayout>
    );
  }

  if (!data) {
    return (
      <MainLayout>
        <div className="vsv-empty-wrap">
          <div className="vsv-empty-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
          </div>
          <p className="vsv-empty-text">No Site Visit Data Found.</p>
          <Link to="/site_visit" className="vsv-back-link">← Back to Site Visits</Link>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="vsv-wrapper">

        {/* ── Header ── */}
        <div className="vsv-header">
          <div className="vsv-header-left">
            <Link to="/site_visit" className="vsv-back-btn">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              Back
            </Link>
            <div className="vsv-header-divider" />
            <div>
              <div className="vsv-breadcrumb">Site Visit / List / View</div>
            </div>
          </div>
          <div className={`vsv-status-badge vsv-status-badge--${data?.status?.toLowerCase()}`}>
            {ToFirstCapital(data?.status || "")}
          </div>
        </div>

        {/* ── Hero Card ── */}
        <div className="vsv-hero-card">
          <div className="vsv-hero-left">
            <div>
              <div className="vsv-hero-label">Project</div>
              <div className="vsv-hero-value">{data?.project?.project_name || "—"}</div>
            </div>
            {data?.otp && (
              <div className="vsv-otp-badge">
                <span className="vsv-otp-label">OTP</span>
                {data.otp}
              </div>
            )}
          </div>
          <div className="vsv-hero-meta">
            <div className="vsv-hero-meta-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              {DateToLocale(data?.site_visit_date_time) || "—"}
            </div>
            <div className="vsv-hero-meta-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
              </svg>
              {data?.user?.name || "—"}
            </div>
            <div className="vsv-hero-meta-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              {ToFirstCapital(data?.site_visit_by) || "—"}
            </div>
          </div>
        </div>

        {/* ── Main Grid ── */}
        <div className="vsv-grid-layout">

          {/* Left Column */}
          <div className="vsv-col">

            {/* User Info */}
            <div className="vsv-card">
              <div className="vsv-card-header">
                <span className="vsv-card-badge">01</span>
                <h2 className="vsv-card-title">User Information</h2>
              </div>
              <div className="vsv-info-grid">
                <div className="vsv-info-item">
                  <span className="vsv-info-label">Full Name</span>
                  <span className="vsv-info-value">{data?.user?.name || "No Data"}</span>
                </div>
                <div className="vsv-info-item">
                  <span className="vsv-info-label">Email</span>
                  <span className="vsv-info-value">{data?.user?.email || "No Data"}</span>
                </div>
                <div className="vsv-info-item">
                  <span className="vsv-info-label">Contact Number</span>
                  <span className="vsv-info-value">
                    {data?.user?.country_code} {data?.user?.contact_number || "No Data"}
                  </span>
                </div>
                <div className="vsv-info-item">
                  <span className="vsv-info-label">Status</span>
                  <span className={`vsv-inline-status vsv-inline-status--${data?.status?.toLowerCase()}`}>
                    {ToFirstCapital(data?.status)}
                  </span>
                </div>
              </div>
            </div>

            {/* Project Details */}
            <div className="vsv-card">
              <div className="vsv-card-header">
                <span className="vsv-card-badge">02</span>
                <h2 className="vsv-card-title">Project Details</h2>
              </div>
              <div className="vsv-info-grid">
                <div className="vsv-info-item">
                  <span className="vsv-info-label">Project Name</span>
                  <span className="vsv-info-value">{data?.project?.project_name || "No Data"}</span>
                </div>
                <div className="vsv-info-item">
                  <span className="vsv-info-label">Site Visit By</span>
                  <span className="vsv-info-value">{ToFirstCapital(data?.site_visit_by) || "No Data"}</span>
                </div>
                <div className="vsv-info-item">
                  <span className="vsv-info-label">Visit Date & Time</span>
                  <span className="vsv-info-value">{DateToLocale(data?.site_visit_date_time) || "No Data"}</span>
                </div>
                <div className="vsv-info-item">
                  <span className="vsv-info-label">No. of Guests</span>
                  <span className="vsv-info-value">{data?.no_of_guest ?? 0}</span>
                </div>
                {!isSelf && (
                  <>
                    <div className="vsv-info-item">
                      <span className="vsv-info-label">Ride Type</span>
                      <span className="vsv-info-value">{ToFirstCapital(data?.ride_type) || "No Data"}</span>
                    </div>
                    <div className="vsv-info-item">
                      <span className="vsv-info-label">Round Trip</span>
                      <span className="vsv-info-value">{data?.is_round_trip ? "Yes" : "No"}</span>
                    </div>
                    <div className="vsv-info-item">
                      <span className="vsv-info-label">Distance</span>
                      <span className="vsv-info-value">{data?.distance || 0} km</span>
                    </div>
                    <div className="vsv-info-item">
                      <span className="vsv-info-label">Estimated Price</span>
                      <span className="vsv-info-value">
                        {data?.estimated_price ? `₹${data.estimated_price}` : "No Data"}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Purpose */}
            {data?.site_visit_purpose && (
              <div className="vsv-card">
                <div className="vsv-card-header">
                  <span className="vsv-card-badge">03</span>
                  <h2 className="vsv-card-title">Visit Purpose</h2>
                </div>
                <p className="vsv-purpose-text">{data.site_visit_purpose}</p>
              </div>
            )}

          </div>

          {/* Right Column */}
          <div className="vsv-col">

            {/* Addresses */}
            <div className="vsv-card">
              <div className="vsv-card-header">
                <span className="vsv-card-badge">04</span>
                <h2 className="vsv-card-title">Location Details</h2>
              </div>
              <div className="vsv-address-row">
                {!isSelf && data?.pickup_address && (
                  <div className="vsv-address-item">
                    <div className="vsv-address-icon">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    </div>
                    <div>
                      <div className="vsv-address-label">Pickup Address</div>
                      <div className="vsv-address-val">{data.pickup_address}</div>
                    </div>
                  </div>
                )}
                <div className="vsv-address-item">
                  <div className="vsv-address-icon">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  </div>
                  <div>
                    <div className="vsv-address-label">
                      {isSelf ? "Project Address" : "Drop Off Address"}
                    </div>
                    <div className="vsv-address-val">
                      {data?.drop_address || data?.project?.street_address || "No Data"}
                    </div>
                  </div>
                </div>
                <div className="vsv-address-item">
                  <div className="vsv-address-icon">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <path d="M9 9h6M9 12h6M9 15h4" />
                    </svg>
                  </div>
                  <div>
                    <div className="vsv-address-label">Project Address</div>
                    <div className="vsv-address-val">{data?.project?.street_address || "No Data"}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Trip Status */}
            <div className="vsv-card">
              <div className="vsv-card-header">
                <span className="vsv-card-badge">05</span>
                <h2 className="vsv-card-title">Trip Status</h2>
              </div>
              <div className="vsv-info-item" style={{ marginBottom: "12px" }}>
                <span className="vsv-info-label">Current Status</span>
                <span className={`vsv-inline-status vsv-inline-status--${data?.status?.toLowerCase()}`} style={{ marginTop: "4px" }}>
                  {ToFirstCapital(data?.status)}
                </span>
              </div>
              <div className="vsv-info-item">
                <span className="vsv-info-label">Update Trip Status</span>
                <div className="vsv-select-wrap" style={{ marginTop: "6px" }}>
                  <select
                    value={tripStatus}
                    onChange={handleStatusChange}
                    className="vsv-select"
                    disabled={isUpdating}
                  >
                    <option value="" disabled>Select Status</option>
                    {["requested", "booked", "cancelled", "completed"].map((s) => (
                      <option key={s} value={s}>
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </option>
                    ))}
                  </select>
                  <svg className="vsv-select-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
                {isUpdating && (
                  <div className="vsv-updating-text">
                    <div className="vsv-updating-dot" />
                    Updating status…
                  </div>
                )}
              </div>
            </div>

            {/* Service Provider — only if not self */}
            {!isSelf && (
              <div className="vsv-card">
                <div className="vsv-card-header">
                  <span className="vsv-card-badge">07</span>
                  <h2 className="vsv-card-title">Service Provider</h2>
                </div>
                {data?.service_provider ? (
                  <div className="vsv-info-grid">
                    <div className="vsv-info-item">
                      <span className="vsv-info-label">Company Name</span>
                      <span className="vsv-info-value">{data.service_provider?.company_name || "No Data"}</span>
                    </div>
                    <div className="vsv-info-item">
                      <span className="vsv-info-label">Contact</span>
                      <span className="vsv-info-value">
                        {data.service_provider?.country_code} {data.service_provider?.contact_number || "No Data"}
                      </span>
                    </div>
                    <div className="vsv-info-item">
                      <span className="vsv-info-label">Email</span>
                      <span className="vsv-info-value">{data.service_provider?.email || "No Data"}</span>
                    </div>
                    <div className="vsv-info-item">
                      <span className="vsv-info-label">Address</span>
                      <span className="vsv-info-value">{data.service_provider?.address || "No Data"}</span>
                    </div>
                  </div>
                ) : (
                  <p className="vsv-no-data">No Service Provider Assigned</p>
                )}
              </div>
            )}

          </div>
        </div>

        {/* ── Agent Route Map — full width ── */}
        {(data?.agent_location?.length > 0 || data?.start_location) && (
          <div className="vsv-card vsv-card--fullwidth" style={{ marginTop: "16px" }}>
            <div className="vsv-card-header">
              <span className="vsv-card-badge">06</span>
              <h2 className="vsv-card-title">Agent Route Map</h2>
            </div>

            {/* Legend */}
            <div className="vsv-map-legend">
              <div className="vsv-map-legend-item">
                <span className="vsv-map-legend-dot vsv-map-legend-dot--start" />
                Start Location
              </div>
              <div className="vsv-map-legend-item">
                <span className="vsv-map-legend-dot vsv-map-legend-dot--trail" />
                Agent Trail
              </div>
              <div className="vsv-map-legend-item">
                <span className="vsv-map-legend-dot vsv-map-legend-dot--last" />
                Last Position
              </div>
              {data?.drop_location?.coordinates?.length === 2 && (
                <div className="vsv-map-legend-item">
                  <span className="vsv-map-legend-dot vsv-map-legend-dot--drop" />
                  Drop Location
                </div>
              )}
            </div>

            <AgentRouteMap
              startLocation={data?.start_location}
              agentLocations={data?.agent_location}
              dropLocation={data?.drop_location}
            />

            {/* Coords summary */}
            <div className="vsv-map-coords">
              {data?.start_location && (
                <div className="vsv-map-coord-item">
                  <span className="vsv-map-coord-label">Start</span>
                  <span className="vsv-map-coord-val">
                    {data.start_location.latitude.toFixed(6)}, {data.start_location.longitude.toFixed(6)}
                  </span>
                </div>
              )}
              {(() => {
                const DUMMY = [[37.421998, -122.084], [37.785834, -122.406417]];
                const isDummy = (lat, lng) => DUMMY.some(([d0, d1]) => Math.abs(lat - d0) < 0.001 && Math.abs(lng - d1) < 0.001);
                const valid = (data?.agent_location || []).filter(p => !isDummy(p.lat, p.lng));
                const last = valid[valid.length - 1];
                return last ? (
                  <div className="vsv-map-coord-item">
                    <span className="vsv-map-coord-label">Last</span>
                    <span className="vsv-map-coord-val">
                      {last.lat.toFixed(6)}, {last.lng.toFixed(6)}
                    </span>
                  </div>
                ) : null;
              })()}
              {data?.drop_location?.coordinates?.length === 2 && (
                <div className="vsv-map-coord-item">
                  <span className="vsv-map-coord-label">Drop</span>
                  <span className="vsv-map-coord-val">
                    {data.drop_location.coordinates[1].toFixed(6)}, {data.drop_location.coordinates[0].toFixed(6)}
                  </span>
                </div>
              )}
              <div className="vsv-map-coord-item">
                <span className="vsv-map-coord-label">Points</span>
                <span className="vsv-map-coord-val">
                  {(data?.agent_location || []).filter(p => {
                    const DUMMY = [[37.421998, -122.084], [37.785834, -122.406417]];
                    return !DUMMY.some(([d0, d1]) => Math.abs(p.lat - d0) < 0.001 && Math.abs(p.lng - d1) < 0.001);
                  }).length} tracked
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Confirm Modal ── */}
      {showConfirm && (
        <div className="vsv-modal-backdrop">
          <div className="vsv-modal">
            <div className="vsv-modal-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <h3 className="vsv-modal-title">Confirm Status Change</h3>
            <p className="vsv-modal-text">
              Are you sure you want to change the trip status to{" "}
              <strong>"{ToFirstCapital(selectedStatus)}"</strong>?
            </p>
            <div className="vsv-modal-actions">
              <button
                className="vsv-btn vsv-btn--confirm"
                onClick={confirmStatusChange}
                disabled={isUpdating}
              >
                Confirm
              </button>
              <button
                className="vsv-btn vsv-btn--cancel"
                onClick={() => setShowConfirm(false)}
                disabled={isUpdating}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default ViewAgentSiteVisit;